import React from "react";
import { useTranslation } from "react-i18next";
import {
  ValidationResult,
  ColumnValidationResult,
  ColumnMatch,
} from "../types/api";

interface ColumnValidationWarningProps {
  validationResult: ValidationResult;
  onColumnFix: (originalColumn: string, newColumn: string) => void;
  onIgnoreWarning: () => void;
  onCancel: () => void;
}

export const ColumnValidationWarning: React.FC<
  ColumnValidationWarningProps
> = ({ validationResult, onColumnFix, onIgnoreWarning, onCancel }) => {
  const { t } = useTranslation("columnValidationWarning");
  const problemColumns = validationResult.column_results.filter(
    (col) => col.status !== "found",
  );

  if (problemColumns.length === 0) {
    return null;
  }

  const renderSuggestion = (
    columnResult: ColumnValidationResult,
    suggestion: ColumnMatch,
  ) => (
    <button
      key={suggestion.ActualName}
      onClick={() =>
        onColumnFix(columnResult.requested_column, suggestion.ActualName)
      }
      style={{
        display: "block",
        width: "100%",
        padding: "8px 12px",
        marginBottom: "4px",
        backgroundColor: "#e3f2fd",
        border: "1px solid #2196f3",
        borderRadius: "4px",
        cursor: "pointer",
        textAlign: "left",
        fontSize: "14px",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "#bbdefb";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "#e3f2fd";
      }}
    >
      <strong>'{suggestion.ActualName}'</strong> (
      {t("percentSimilar", { percent: (suggestion.Similarity * 100).toFixed(1) })})
    </button>
  );

  return (
    <div
      style={{
        border: "2px solid #ff9800",
        borderRadius: "8px",
        backgroundColor: "#fff3e0",
        padding: "20px",
        marginBottom: "20px",
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}
      >
        <span style={{ fontSize: "24px", marginRight: "10px" }}>⚠️</span>
        <h3 style={{ margin: 0, color: "#e65100" }}>
          {t("heading")}
        </h3>
      </div>

      <p style={{ marginBottom: "20px", color: "#bf360c" }}>
        <strong>{t("intro.important")}</strong> {t("intro.text")}{" "}
        <strong>{t("intro.exposureWarning")}</strong>. {t("intro.suffix")}
      </p>

      {problemColumns.map((columnResult) => (
        <div
          key={columnResult.requested_column}
          style={{
            backgroundColor: "white",
            border: "1px solid #ffcc02",
            borderRadius: "6px",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          <div style={{ marginBottom: "12px" }}>
            <strong style={{ color: "#d84315" }}>{t("columnNotFound")}</strong>{" "}
            <code
              style={{
                backgroundColor: "#ffebee",
                padding: "2px 6px",
                borderRadius: "3px",
              }}
            >
              {columnResult.requested_column}
            </code>
          </div>

          {columnResult.status === "similar_found" &&
            columnResult.suggestions &&
            columnResult.suggestions.length > 0 && (
              <div>
                <div
                  style={{
                    marginBottom: "8px",
                    fontWeight: "bold",
                    color: "#1565c0",
                  }}
                >
                  {t("didYouMean")}
                </div>
                {columnResult.suggestions
                  .slice(0, 3)
                  .map((suggestion) =>
                    renderSuggestion(columnResult, suggestion),
                  )}
              </div>
            )}

          {columnResult.status === "not_found" && (
            <div style={{ color: "#d32f2f", fontStyle: "italic" }}>
              {t("noSimilarFound")}{" "}
              {validationResult.available_columns.join(", ")}
            </div>
          )}
        </div>
      ))}

      <div
        style={{
          marginTop: "20px",
          display: "flex",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={onCancel}
          style={{
            padding: "10px 20px",
            backgroundColor: "#2196f3",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          {t("fixColumnNames")}
        </button>
        <button
          onClick={onIgnoreWarning}
          style={{
            padding: "10px 20px",
            backgroundColor: "#ff5722",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          {t("proceedAnyway")}
        </button>
      </div>

      <div
        style={{
          marginTop: "16px",
          padding: "12px",
          backgroundColor: "#e8f5e8",
          border: "1px solid #4caf50",
          borderRadius: "4px",
          fontSize: "14px",
        }}
      >
        <strong>{t("proTip.label")}</strong> {t("proTip.text")} "
        {validationResult.available_columns.slice(0, 3).join('", "')}"
      </div>
    </div>
  );
};
